import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Target, TrendingUp, CheckCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#002255', '#FF6600', '#3B82F6', '#10B981', '#F59E0B'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002255]"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tableau de bord
        </h1>
        <p className="text-gray-600">Vue d'ensemble de vos activités</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="stat-card" data-testid="stat-contacts">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Total Contacts</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.total_contacts || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-opportunites">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-[#FF6600]" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Opportunités</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.total_opportunites || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-chaudes">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Opportunités Chaudes</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.opportunites_chaudes || 0}</p>
        </div>

        <div className="stat-card" data-testid="stat-gagnees">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-1">Opportunités Gagnées</p>
          <p className="text-3xl font-bold text-[#002255]">{stats?.opportunites_gagnees || 0}</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="stat-card">
          <h3 className="text-lg font-semibold text-[#002255] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Répartition par Statut
          </h3>
          {stats?.repartition_statuts && stats.repartition_statuts.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.repartition_statuts}
                  dataKey="count"
                  nameKey="_id"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {stats.repartition_statuts.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">Aucune donnée disponible</p>
          )}
        </div>

        {/* Category Distribution */}
        <div className="stat-card">
          <h3 className="text-lg font-semibold text-[#002255] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Répartition par Catégorie
          </h3>
          {stats?.repartition_categories && stats.repartition_categories.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.repartition_categories}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="_id" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#002255" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">Aucune donnée disponible</p>
          )}
        </div>
      </div>

      {/* Revenue Card */}
      <div className="stat-card mt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-600 mb-2">Montant Total Estimé</p>
            <p className="text-4xl font-bold text-[#002255]">
              {new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'XOF',
                minimumFractionDigits: 0
              }).format(stats?.montant_total_estime || 0)}
            </p>
          </div>
          <div className="w-16 h-16 bg-[#FF6600]/10 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-[#FF6600]" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
